import memoize from 'lodash/memoize'

import { createSelector } from '@app/utils/createSelector'
import { invariant } from '@app/utils/invariant'
import moment from '@app/utils/moment'
import { catchFetchDataError } from '@app/utils/performFetchData'

import { getPerks } from '@app/packages/api/v2/actions/perks'
import { getUsersByIdPerks } from '@app/packages/api/v2/actions/users'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'
import { perksSelector } from '@app/store/slices/entities'
import { profileUserSelector } from '@app/store/slices/profile'
import { routingDateSelector } from '@app/store/slices/routing'
import { setUserPerksSlice, usersPerksSelector } from '@app/store/slices/users'

export const perksMetaSlice = createReduxSlice<{ loaded_at: number }>('perks')

export const perksMapResultSelector = createSelector([perksMetaSlice.selector, routingDateSelector, perksSelector], (meta, date, perks) =>
  createLoadable(c => {
    if (!meta || moment.unix(meta.loaded_at).add(30, 'minutes').isBefore(date)) {
      return c.load(
        createPromise('Perks must be fetched', date.format(), async dispatch => {
          await dispatch(callV2Api(getPerks())).catch(catchFetchDataError(dispatch, 'Perks fetch failed'))
          dispatch(perksMetaSlice.set({ loaded_at: date.unix() }))
        })
      )
    }
    return perks
  })
)
export const perksListResultSelector = mapThunkSelector(perksMapResultSelector, map => Object.values(map))

export const createPerksByIdsResultSelector = (idsSelector: (state: unknown) => string[]) =>
  createSelector([perksMapResultSelector, idsSelector], (perksResult, ids) =>
    createLoadable(c => {
      const perks = c.unwrap(perksResult)
      return ids.map(id => {
        const perk = perks[id]
        if (!perk) throw new Error(`Perk with id ${id} not found`)
        return perk
      })
    })
  )

const createUserMetaKey = memoize((id: string) => createReduxSlice<{ route_uuid: string }>(`perks_user_${id}`))

const createPerksIdsByUserIdResultSelector = (renderIdSelector: () => string, idSelector: (state: unknown) => string | null) => {
  const metaKeySelector = createSelector([idSelector], id => {
    if (!id) return null
    return createUserMetaKey(id)
  })
  const metaKeyDataSelector = (state: unknown) => metaKeySelector(state)?.selector(state)

  return createSelector(
    [metaKeySelector, metaKeyDataSelector, renderIdSelector, (state: unknown) => usersPerksSelector(state), idSelector],
    (meta, metaData, uuid, perks, id) =>
      createLoadable(c => {
        if (!id) return []
        if (!meta) throw new Error('User meta is missing')
        if (!metaData || metaData.route_uuid !== uuid) {
          return c.load(
            createPromise(`Perks for user ${id} must be fetched`, uuid, async dispatch => {
              const resp = await dispatch(callV2Api(getUsersByIdPerks(id))).catch(catchFetchDataError(dispatch, 'Perks fetch failed'))
              dispatch(
                setUserPerksSlice(
                  id,
                  resp.data.map(p => p.id)
                )
              )
              dispatch(meta.set({ route_uuid: uuid }))
            })
          )
        }
        const userPerks = perks[id]
        if (!userPerks) throw new Error(`Perks for user ${id} are missing`)
        return userPerks
      })
  )
}

export const createPerksByUserIdResultSelector = (renderIdSelector: () => string, idSelector: (state: unknown) => string | null) => {
  const idsResultSelector = createPerksIdsByUserIdResultSelector(renderIdSelector, idSelector)
  return createSelector([perksMapResultSelector, idsResultSelector], (perksResult, idsResult) =>
    createLoadable(c => {
      const perks = c.unwrap(perksResult)
      const ids = c.unwrap(idsResult)
      return ids.map(id => perks[id] ?? invariant(new Error(`Perk #${id} not found`)))
    })
  )
}

export const profilePerksResultSelector = createPerksByUserIdResultSelector(
  () => 'profile',
  state => profileUserSelector(state)?.id ?? null
)
